/* global EpgEnabled ShowcaseRedesign */
import React, { Component } from 'react';
import _ from 'lodash';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Link, withRouter, browserHistory } from 'react-router';
import Dotdotdot from 'react-dotdotdot';
import { getTimestamp } from 'helpers/DateTimeHelper';
import Badge from 'components/Badge';
import ProgressBar from 'components/ProgressBar';
import MoreInfoButton from 'components/MoreInfoButton';
import Timer from 'components/Timer';
import { isDescTooLong, isSportsContent, truncateText } from 'helpers/ContentHelper';
import {
  SITE_PLATFORM_WEB_LARGE,
  SITE_PLATFORM_WEB,
  SITE_PLATFORM_TABLET,
  SITE_PLATFORM_MOBILE,
} from 'helpers/BreakpointHelper';
import { countdownBannerEnabled } from 'helpers/ConfigHelper';
import {
  getSegmentModelMetadata,
  getTealiumRedesignEvents,
  handleTealiumButtonAction,
  SEGMENT_HERO_COLLECTION_TITLE,
  setSegmentContextSourceLive,
  setSegmentRestartedPlaybackGlobal,
  setSegmentResumedPlaybackGlobal,
  handleAnalytics,
  Events,
} from '@foxcorp/delta-web-analytics';
import { logPlayerEvent } from 'helpers/LogReportHelpers';
import { COLLECTION_HERO_LOGO_IMAGE, renderImage, getDynamicImage } from 'helpers/ImageHelper';
import { buildInfoTags } from './InfoTagHelper';
import InfoTagStyle from './InfoTags.module.scss';

const BREAKPOINTS = {
  [SITE_PLATFORM_WEB_LARGE]: {
    canDisplayRestartButton: true,
    enableTruncateDesc: false,
    displayDescription: true,
    displayLocationRequired: true,
    titleLines: 2,
  },
  [SITE_PLATFORM_WEB]: {
    canDisplayRestartButton: true,
    enableTruncateDesc: false,
    displayDescription: true,
    displayLocationRequired: true,
    overflowCtaEnabled: false,
    titleLines: 2,
  },
  [SITE_PLATFORM_TABLET]: {
    canDisplayRestartButton: true,
    enableTruncateDesc: false,
    displayDescription: true,
    displayLocationRequired: true,
    overflowCtaEnabled: false,
    titleLines: 2,
  },
  [SITE_PLATFORM_MOBILE]: {
    canDisplayRestartButton: true,
    enableTruncateDesc: false, // Disable 'More Info' CTA
    displayDescription: false,
    displayLocationRequired: true,
    overflowCtaEnabled: true,
    titleLines: 3,
  },
};
const SLIDE_THRESHOLD = 50;
let slideDragged = false;
let leftBound;

class InfoTags extends Component {
  componentDidMount() {
    this.heroClickthrough.addEventListener('mousedown', this.heroClickMouseDown);
    this.heroClickthrough.addEventListener('mouseup', this.heroClickMouseUp);
  }

  componentWillUnmount() {
    this.heroClickthrough.removeEventListener('mousedown', this.heroClickMouseDown);
    this.heroClickthrough.removeEventListener('mouseup', this.heroClickMouseUp);
  }

  heroClickMouseDown = () => {
    slideDragged = false;
    leftBound = this.heroClickthrough.getBoundingClientRect().left;
  };

  heroClickMouseUp = () => {
    const newLeftBound = this.heroClickthrough.getBoundingClientRect().left;
    if (Math.abs(newLeftBound - leftBound) > SLIDE_THRESHOLD) slideDragged = true;
  };

  renderTimer = ({ countdownTime }) => {
    const { deactivateTimer } = this.props;
    return <Timer countdownTime={countdownTime} setIsActive={deactivateTimer} />;
  };

  getEventLogo = eventData => {
    const { breakpoint } = this.props;
    return getDynamicImage({
      breakpoint,
      context: COLLECTION_HERO_LOGO_IMAGE,
      url: eventData?.keyArt.logo,
    });
  };

  render() {
    const {
      account,
      breakpoint,
      callCollectionImpressionEvent,
      collectionInfo,
      foxAwesomeLayout,
      headerImageState,
      idx,
      isLive,
      isDescriptionOpen,
      item,
      onPrimaryButtonClick,
      onTitleClick,
      onWatch,
      overridePlayButtonClick,
      overridePlayButtonLink,
      router,
      saveLiveRestart,
      saveLiveVideo,
      setSuggestedChannel,
      smallCollectionHeader,
      style,
      useSeriesLogo,
      timerActive,
      toggleDescription,
    } = this.props;

    const {
      canDisplayRestartButton,
      displayDescription,
      enableTruncateDesc,
      titleLines,
    } = BREAKPOINTS[breakpoint];
    const { global_watchButton: watchButtonText } = window.Language;

    const {
      badge: itemBadge,
      callSign,
      description: itemDescription,
      eventData,
      headline,
      alternativeHeadline,
      endDate,
      fadeButtons,
      isSeriesDetailAvailable,
      name,
      originalVideo,
      previewLink,
      seriesLogo: itemSeriesLogo,
      seriesName,
      startDate,
      tabIndex,
      title,
      titleLink,
      videoId,
    } = item;
    const description = timerActive ? eventData?.description : itemDescription;
    const seriesLogo = timerActive ? this.getEventLogo(eventData) : itemSeriesLogo;
    const badge = timerActive ? null : itemBadge;

    const showTimer = countdownBannerEnabled() && timerActive;

    const infotagHelperProps = {
      canDisplayRestartButton,
      foxAwesomeLayout,
      isDescriptionOpen,
      isSeriesDetailAvailable,
      item: { ...item, description },
      style,
      toggleDescription,
    };
    const {
      descriptionComponent,
      highlightClass,
      highlightText,
      isPreviewSecondaryBtn,
      isRestartBtn,
      isRestartSecondaryBtn,
      isResumeBtn,
      isWatchBtn,
      isWatchLiveSecondaryBtn,
      meta,
      primaryButtonIsPlayButton,
      primaryButtonLink,
      primaryButtonText,
      secondaryButtonClasses,
      secondaryButtonLink,
      secondaryButtonText,
      showName,
      showProgressBar,
      tertiaryButtonLink,
      tertiaryButtonText,
      tertiaryButtonClasses,
    } = buildInfoTags({ ...infotagHelperProps, canDisplayRestartButton });

    const tealiumRedesignEvents = getTealiumRedesignEvents({ account });
    const itemIsSportsContent = isSportsContent(item);
    const isMobile = breakpoint === SITE_PLATFORM_MOBILE;

    let badgeTag = null;
    if (badge) {
      badgeTag = (
        <Badge
          theme={style}
          badge={badge}
          title={title}
          startDate={getTimestamp(startDate)}
          tabIndex={tabIndex}
        />
      );
    }
    const progressBarComponent =
      !timerActive && showProgressBar ? (
        <ProgressBar endDate={endDate} startDate={startDate} theme={style} />
      ) : null;

    const primaryButtonClasses = [style.showButton];
    if (fadeButtons) primaryButtonClasses.push(style.fadeButtons);

    const handleLiveClick = () => {
      setSegmentContextSourceLive(true);
      if (EpgEnabled) {
        setSuggestedChannel({ network: callSign, videoId });
      } else {
        saveLiveVideo(originalVideo);
      }
    };

    const handleTitleClick = () => {
      callCollectionImpressionEvent();
      if (itemIsSportsContent) handleLiveClick();
      onTitleClick({});
    };

    const tealiumCollectionEvents = ({
      headline,
      collectionType,
      totalItems,
      totalCollections,
    }) => {
      tealiumRedesignEvents.heroItemSelect({
        account,
        type: 'hero',
        state: 'autoplay video',
        containerTitle: name,
        verticalPosition: '1',
        horizontalPosition: idx + 1,
        title,
        subtitle: secondaryButtonText,
        cc: 'cc off',
        volume: 'volume off',
        collectionTitle: headline,
        collectionType,
        playlistLength: totalItems,
        collectionCount: totalCollections,
        item,
      });
    };

    const handleSegmentItemClick = buttonText => {
      const props = {
        page_action_type: buttonText,
        page_collection_count: collectionInfo.totalCollections,
        page_collection_id: collectionInfo.panelId,
        page_collection_title: SEGMENT_HERO_COLLECTION_TITLE,
        page_collection_type: collectionInfo.collectionType,
        page_item_type: 'hero',
        page_item_state: headerImageState,
        page_item_container_title: title,
        page_item_vertical_position: '1',
        page_item_horizontal_position: idx + 1,
        page_item_title: buttonText,
        page_item_subtitle: highlightText,
        page_item_cc: 'cc off',
        page_item_volume: 'volume off',
        page_playlist_length: collectionInfo.totalItems,
        page_recs_collection: collectionInfo.isPanelRecommended,
        page_video_uid: collectionInfo.uID,
        ...getSegmentModelMetadata(),
      };
      handleAnalytics({
        event: Events.HERO_ITEM_SELECT_ACTION,
        payload: props,
      });
    };

    const handlePrimaryClick = e => {
      const buttonText = Array.isArray(primaryButtonText)
        ? primaryButtonText[1]
        : primaryButtonText;
      handleSegmentItemClick(buttonText);
      callCollectionImpressionEvent();
      if (primaryButtonIsPlayButton) {
        logPlayerEvent('onVideoPlayClick', { video_id: videoId, video_name: name });
        onPrimaryButtonClick(e);
      }
      if (isLive) handleLiveClick();
      if (onWatch) onWatch();
      handleTealiumButtonAction({
        text: watchButtonText,
        title,
        subtitle: name,
        account,
        isRestartBtn,
        isWatchBtn,
      });
      if (isResumeBtn) {
        setSegmentResumedPlaybackGlobal(true);
      }
      if (isRestartBtn) {
        saveLiveRestart({ video: item });
        setSegmentRestartedPlaybackGlobal(true);
      }
      tealiumCollectionEvents(collectionInfo);
    };

    const handleSecondaryClick = () => {
      handleSegmentItemClick(secondaryButtonText);
      const { toggleVideoModal } = this.props;
      callCollectionImpressionEvent();
      if (isLive) handleLiveClick();
      if (onWatch) onWatch();
      handleTealiumButtonAction({
        text: secondaryButtonText,
        title,
        subtitle: name,
        account,
        isWatchLiveSecondaryBtn,
      });
      if (isRestartSecondaryBtn) {
        saveLiveRestart({ video: item });
        setSegmentRestartedPlaybackGlobal(true);
      }
      if (isPreviewSecondaryBtn) {
        toggleVideoModal({ url: previewLink, videoData: item, panelIdx: idx });
      }
      tealiumCollectionEvents(collectionInfo);
    };

    const handleTertiaryClick = () => {
      handleSegmentItemClick(tertiaryButtonText);
      if (onWatch) onWatch();
      handleTealiumButtonAction({
        text: tertiaryButtonText,
        title,
        subtitle: name,
        account,
      });
    };

    const renderCTAs = () => {
      const hasActionButtons = !_.isEmpty(displayActionButtons);
      const desktopCTAs = (
        <div className={classNames(InfoTagStyle.desktopCTAContainer, InfoTagStyle.buttons)}>
          {hasActionButtons &&
            _.map(displayActionButtons, (button, index) => (
              <Link
                key={button.text}
                to={button.link}
                onClick={button.onClick}
                className={classNames([button.classes])}
                data-testid={button.dataTest}
                aria-label={
                  Array.isArray(button.text)
                    ? `${button.text[1]} ${showName}`
                    : `${button.text} ${showName}`
                }
                role={button.role}
                aria-hidden={isMobile}
                tabIndex={tabIndex}
              >
                {button.text}
              </Link>
            ))}
          {isDescTooLong(description) && enableTruncateDesc ? (
            <MoreInfoButton
              key={moreInfoKey}
              className={style.moreLink}
              value={moreInfoText}
              description={description}
              ariaHidden={isMobile}
              tabIndex={tabIndex}
            />
          ) : null}
        </div>
      );

      let mobileCTAs = null;
      if (button) {
        mobileCTAs = (
          <div className={classNames(InfoTagStyle.mobileCTAContainer, InfoTagStyle.buttons)}>
            <Link
              key={button.text}
              to={button.link}
              role="button"
              onClick={button.onClick}
              className={classNames(button.classes)}
              data-testid={button.dataTest}
              aria-hidden={!isMobile}
              aria-label={
                Array.isArray(button.text)
                  ? `${button.text[1]} ${showName}`
                  : `${button.text} ${showName}`
              }
              tabIndex={tabIndex}
            >
              {button.text}
            </Link>
          </div>
        );
      }
      return (
        <>
          {desktopCTAs}
          {mobileCTAs}
        </>
      );
    };
    const isOverridePlayButton = overridePlayButtonLink && primaryButtonIsPlayButton;
    const actionButtons = [];
    actionButtons.push({
      link: !isOverridePlayButton && primaryButtonLink,
      onClick: isOverridePlayButton
        ? () => overridePlayButtonClick({ visibility: true, redirectUrl: primaryButtonLink })
        : handlePrimaryClick,
      classes: primaryButtonClasses,
      text: primaryButtonText,
      dataTest: 'home-slider-primary-button',
      role: 'button',
    });
    actionButtons.push({
      link: secondaryButtonLink,
      onClick: handleSecondaryClick,
      classes: secondaryButtonClasses,
      text: secondaryButtonText,
      dataTest: 'home-slider-secondary-button',
      role: 'button',
    });
    actionButtons.push({
      link: tertiaryButtonLink,
      onClick: handleTertiaryClick,
      classes: tertiaryButtonClasses,
      text: tertiaryButtonText,
      role: 'button',
    });
    const displayActionButtons = _.filter(actionButtons, a => !!a.text);

    const foxAwesomeContainerStyles = [style.infoTagsRedesign];
    const truncateLength = 30;
    const kicker = (
      <div className={style.kicker}>{truncateText(alternativeHeadline, truncateLength)}</div>
    );
    const headLineText = (
      <Dotdotdot clamp={2} useNativeClamp>
        {headline}
      </Dotdotdot>
    );

    const isSeriesLogoImage = useSeriesLogo && seriesLogo;

    foxAwesomeContainerStyles.push(style.smallerInfoContainer);
    if (smallCollectionHeader) foxAwesomeContainerStyles.push(style.withSmallHeader);

    const isPromo = _.get(item, 'originalVideo.@type') === 'Promo';

    const seriesLogoOrText = isSeriesLogoImage ? (
      renderImage({
        alt: seriesName,
        breakpoint,
        className: style.seriesLogo,
        dataTest: 'info-tags-series-logo',
        imageType: COLLECTION_HERO_LOGO_IMAGE,
        src: seriesLogo,
        'aria-label': showName,
        role: 'link',
      })
    ) : (
      <Dotdotdot clamp={titleLines} useNativeClamp aria-label={showName} role="link">
        {showName}
      </Dotdotdot>
    );
    const moreInfoText = _.get(window.Language, 'global_moreInfoButton');
    const moreInfoKey = moreInfoText.toLowerCase(_.replace(' ', '-'));
    const button = displayActionButtons[0];
    const networksBar = window.ExternalConfig.networkNavigation;
    const promoTypeIsShowcaseBurntIn = originalVideo?.promoType === 'showcaseBurntIn';
    const heroPlayerCTAClasses = classNames(
      style.heroPlayerCTA,
      networksBar ? style.withNetworksBar : null,
      !promoTypeIsShowcaseBurntIn ? style.heroPlayerNation : null,
      promoTypeIsShowcaseBurntIn ? style.heroPlayerCTANationBurntInImage : null,
    );

    const showKicker = ShowcaseRedesign && alternativeHeadline && isPromo;

    return (
      <>
        <div
          ref={elem => {
            this.heroClickthrough = elem;
          }}
          draggable={false}
          className={heroPlayerCTAClasses}
          onClick={e => {
            browserHistory.push(button?.link);
            if (slideDragged || !promoTypeIsShowcaseBurntIn) return;
            const link = promoTypeIsShowcaseBurntIn ? originalVideo.url : button?.link;
            e.target.href = link;
            if (!promoTypeIsShowcaseBurntIn) {
              router.push(link);
              button.onClick(e);
            } else {
              window.location = link;
            }

            e.stopPropagation();
          }}
        />
        {!promoTypeIsShowcaseBurntIn ? (
          <Link
            to={button?.link}
            onClick={onPrimaryButtonClick}
            className={classNames([style.infoContainer, ...foxAwesomeContainerStyles])}
            tabIndex={tabIndex}
          >
            {showKicker ? kicker : badgeTag}
            <div className={style.infoTagsRedesignContainer}>
              {ShowcaseRedesign && headline && isPromo ? (
                <div className={style.headline}>{headLineText}</div>
              ) : (
                <Link
                  data-test="info-tags-title-link"
                  key="homeTitleLink"
                  onClick={handleTitleClick}
                  to={titleLink}
                  className={style.showName}
                  tabIndex={tabIndex}
                >
                  {seriesLogoOrText}
                </Link>
              )}
              <div className={style.description}>
                <span className={highlightClass} data-test="info-tags-highlight">
                  {highlightText}
                </span>
                {showTimer && this.renderTimer({ countdownTime: eventData?.startTime })}
                {displayDescription && descriptionComponent}
                {progressBarComponent}
                {!showTimer && (
                  <div className={style.detailContainer}>
                    <span className={style.meta}>{meta}</span>
                  </div>
                )}
              </div>
              {renderCTAs()}
            </div>
          </Link>
        ) : null}
      </>
    );
  }
}

InfoTags.defaultProps = {
  account: null,
  breakpoint: null,
  callCollectionImpressionEvent: () => {},
  collectionInfo: {},
  deactivateTimer: () => {},
  idx: null,
  foxAwesomeLayout: false,
  headerImageState: null,
  isLive: false,
  isDescriptionOpen: false,
  location: {},
  onPrimaryButtonClick: () => {},
  onWatch: () => {},
  overridePlayButtonClick: () => {},
  overridePlayButtonLink: false,
  smallCollectionHeader: false,
  style: {},
  timerActive: false,
  toggleVideoModal: () => {},
  toggleDescription: () => {},
  useSeriesLogo: false,
};

InfoTags.propTypes = {
  account: PropTypes.shape({}),
  breakpoint: PropTypes.string,
  callCollectionImpressionEvent: PropTypes.func,
  collectionInfo: PropTypes.shape({}),
  deactivateTimer: PropTypes.func,
  foxAwesomeLayout: PropTypes.bool,
  headerImageState: PropTypes.string,
  idx: PropTypes.number,
  isLive: PropTypes.bool,
  isDescriptionOpen: PropTypes.bool,
  item: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  location: PropTypes.shape({}),
  onPrimaryButtonClick: PropTypes.func,
  onTitleClick: PropTypes.func.isRequired,
  onWatch: PropTypes.func,
  overridePlayButtonClick: PropTypes.func,
  overridePlayButtonLink: PropTypes.bool,
  router: PropTypes.shape({}).isRequired,
  saveLiveRestart: PropTypes.func.isRequired,
  saveLiveVideo: PropTypes.func.isRequired,
  setSuggestedChannel: PropTypes.func.isRequired,
  smallCollectionHeader: PropTypes.bool,
  style: PropTypes.shape({}),
  timerActive: PropTypes.bool,
  toggleVideoModal: PropTypes.func,
  toggleDescription: PropTypes.func,
  useSeriesLogo: PropTypes.bool,
};

export default withRouter(InfoTags);
